import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const EarningsChart = ({ monthlyData = [] }) => {
  const [timeRange, setTimeRange] = useState('year');

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const chartData = monthlyData?.map((data, index) => ({
    month: monthNames?.[index],
    earnings: data?.earnings || 0,
    withdrawals: data?.withdrawals || 0,
    net: (data?.earnings || 0) - (data?.withdrawals || 0)
  }));

  const lastSixMonths = chartData?.slice(-6) || [];
  const displayData = timeRange === 'year' ? chartData : lastSixMonths;

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4 mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
            Monthly Income Analytics
          </h2>
          <p className="text-sm text-muted-foreground">
            Track your earnings and withdrawal patterns
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTimeRange('6months')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-md text-xs md:text-sm font-medium transition-smooth ${
              timeRange === '6months' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            6 Months
          </button>
          <button
            onClick={() => setTimeRange('year')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-md text-xs md:text-sm font-medium transition-smooth ${
              timeRange === 'year' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Year
          </button>
        </div>
      </div>

      <div className="w-full h-64 md:h-80" aria-label="Monthly Earnings Chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={displayData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `₱${(value / 1000)?.toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              formatter={(value) => [`₱${value?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, '']}
            />
            <Legend 
              wrapperStyle={{ fontSize: '14px' }}
              iconType="circle"
            />
            <Bar 
              dataKey="earnings" 
              fill="var(--color-primary)" 
              radius={[6, 6, 0, 0]}
              name="Earnings"
            />
            <Bar 
              dataKey="withdrawals" 
              fill="var(--color-warning)" 
              radius={[6, 6, 0, 0]}
              name="Withdrawals"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-xs md:text-sm text-muted-foreground mb-1">Total Earnings</p>
          <p className="text-lg md:text-xl font-bold text-primary">
            ₱{displayData?.reduce((sum, d) => sum + (d?.earnings || 0), 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs md:text-sm text-muted-foreground mb-1">Total Withdrawals</p>
          <p className="text-lg md:text-xl font-bold text-warning">
            ₱{displayData?.reduce((sum, d) => sum + (d?.withdrawals || 0), 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs md:text-sm text-muted-foreground mb-1">Net Income</p>
          <p className="text-lg md:text-xl font-bold text-success">
            ₱{displayData?.reduce((sum, d) => sum + (d?.net || 0), 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EarningsChart;